import Phaser from "phaser";
import type { PlayerState } from "../runtime/playerState";

export class ConfigView {
  private container: Phaser.GameObjects.Container;
  private resolver: (() => void) | null = null;
  private valueTexts = new Map<string, Phaser.GameObjects.Text>();

  constructor(private scene: Phaser.Scene, private state: PlayerState) {
    this.container = scene.add.container(0, 0).setVisible(false).setDepth(100);
  }

  open(onChange?: () => void): Promise<void> {
    this.cleanup();
    const w = this.scene.scale.width;
    const h = this.scene.scale.height;

    const dim = this.scene.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0.86);
    this.container.add(dim);

    const title = this.scene.add
      .text(w / 2, 60, "CONFIG", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "32px",
        color: "#ffffff",
      })
      .setOrigin(0.5);
    this.container.add(title);

    type Row = {
      key: string;
      label: string;
      get: () => number;
      set: (v: number) => void;
      min: number;
      max: number;
      step: number;
      format: (v: number) => string;
    };
    const rows: Row[] = [
      {
        key: "textSpeed",
        label: "テキスト速度 (ms/字)",
        get: () => this.state.config.textSpeed,
        set: (v) => this.state.setConfig({ textSpeed: v }),
        min: 0,
        max: 80,
        step: 4,
        format: (v) => (v === 0 ? "瞬時" : `${v} ms`),
      },
      {
        key: "autoDelay",
        label: "オート進行 (ms)",
        get: () => this.state.config.autoDelayMs,
        set: (v) => this.state.setConfig({ autoDelayMs: v }),
        min: 200,
        max: 5000,
        step: 100,
        format: (v) => `${v} ms`,
      },
      {
        key: "bgm",
        label: "BGM 音量 (%)",
        get: () => Math.round(this.state.config.bgmVolume * 100),
        set: (v) => this.state.setConfig({ bgmVolume: v / 100 }),
        min: 0,
        max: 100,
        step: 5,
        format: (v) => `${v}%`,
      },
      {
        key: "se",
        label: "SE 音量 (%)",
        get: () => Math.round(this.state.config.seVolume * 100),
        set: (v) => this.state.setConfig({ seVolume: v / 100 }),
        min: 0,
        max: 100,
        step: 5,
        format: (v) => `${v}%`,
      },
      {
        key: "voice",
        label: "ボイス音量 (%)",
        get: () => Math.round(this.state.config.voiceVolume * 100),
        set: (v) => this.state.setConfig({ voiceVolume: v / 100 }),
        min: 0,
        max: 100,
        step: 5,
        format: (v) => `${v}%`,
      },
    ];

    const startY = 130;
    const rowH = 68;
    rows.forEach((row, i) => {
      const y = startY + i * rowH;

      const label = this.scene.add.text(80, y, row.label, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "18px",
        color: "#cce6ff",
      });
      const valueText = this.scene.add
        .text(w - 80, y, row.format(row.get()), {
          fontFamily: "system-ui, sans-serif",
          fontSize: "20px",
          color: "#ffffff",
        })
        .setOrigin(1, 0);
      this.valueTexts.set(row.key, valueText);

      // minus / plus
      const makeBtn = (xOffset: number, sign: number, char: string) => {
        const x = w - 220 + xOffset;
        const btnBg = this.scene.add
          .rectangle(x, y + 12, 40, 40, 0x224466, 0.9)
          .setStrokeStyle(1, 0x66aaff)
          .setInteractive({ useHandCursor: true });
        const btnLbl = this.scene.add
          .text(x, y + 12, char, {
            fontFamily: "system-ui, sans-serif",
            fontSize: "22px",
            color: "#ffffff",
          })
          .setOrigin(0.5);
        btnBg.on("pointerdown", () => {
          const cur = row.get();
          const next = Math.max(row.min, Math.min(row.max, cur + sign * row.step));
          row.set(next);
          valueText.setText(row.format(next));
          onChange?.();
        });
        btnBg.on("pointerover", () => btnBg.setFillStyle(0x336699, 1.0));
        btnBg.on("pointerout", () => btnBg.setFillStyle(0x224466, 0.9));
        this.container.add([btnBg, btnLbl]);
      };
      makeBtn(0, -1, "−");
      makeBtn(56, +1, "+");

      this.container.add([label, valueText]);
    });

    // close
    const closeBg = this.scene.add
      .rectangle(w / 2, h - 60, 200, 44, 0x224466, 0.95)
      .setStrokeStyle(2, 0x66aaff)
      .setInteractive({ useHandCursor: true });
    const closeLbl = this.scene.add
      .text(w / 2, h - 60, "閉じる", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "18px",
        color: "#ffffff",
      })
      .setOrigin(0.5);
    closeBg.on("pointerdown", () => this.resolveAndClose());
    closeBg.on("pointerover", () => closeBg.setFillStyle(0x336699, 1.0));
    closeBg.on("pointerout", () => closeBg.setFillStyle(0x224466, 0.95));
    this.container.add([closeBg, closeLbl]);

    this.container.setVisible(true);
    return new Promise((resolve) => {
      this.resolver = resolve;
    });
  }

  private resolveAndClose(): void {
    const r = this.resolver;
    this.resolver = null;
    this.cleanup();
    this.container.setVisible(false);
    if (r) r();
  }

  private cleanup(): void {
    this.container.each((c: Phaser.GameObjects.GameObject) => c.destroy());
    this.container.removeAll(false);
    this.valueTexts.clear();
  }
}
